import { Briefcase, CalendarDays, Wifi, Coffee } from "lucide-react";
import Reveal from "./Reveal";
import { BUSINESS_ROLES, CONTACT, IMAGES } from "@/data/content";

const IMG = IMAGES.schlafzimmer;

const HIGHLIGHTS = [
    {
        icon: Briefcase,
        title: "Arbeitsplatz inklusive",
        text: "Schreibtisch im Schlafzimmer – ideal für Homeoffice und Dokumentation.",
    },
    {
        icon: Wifi,
        title: "Schnelles WLAN",
        text: "Kostenlos in der gesamten Wohnung – für Calls, Cloud & Streaming.",
    },
    {
        icon: Coffee,
        title: "Voll ausgestattete Küche",
        text: "Selbst kochen statt Restaurant – Herd, Kühlschrank, Kaffeemaschine, Geschirrspüler.",
    },
    {
        icon: CalendarDays,
        title: "Mindestaufenthalt 3 Nächte",
        text: "Optimiert für Projekt- und Montageeinsätze. Langzeitvorteile auf Anfrage.",
    },
];

export default function BusinessTravelers() {
    return (
        <section
            id="berufsreisende"
            data-testid="business-section"
            className="section-y relative bg-muted/40"
        >
            <div className="container-em grid lg:grid-cols-12 gap-10 md:gap-16 items-center">
                <div className="lg:col-span-7">
                    <Reveal>
                        <span className="font-accent text-xs uppercase tracking-[0.3em] text-primary">
                            Für Berufsreisende
                        </span>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h2
                            data-testid="business-title"
                            className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-balance"
                        >
                            Ideal für{" "}
                            <em className="not-italic text-primary">
                                Berufsreisende
                            </em>
                            .
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-6 text-foreground/75 text-lg leading-relaxed max-w-2xl">
                            Unsere Ferienwohnung eignet sich ideal für
                            Projektleiter, Bauleiter, Ingenieure,
                            Servicetechniker, Außendienstmitarbeiter,
                            Geschäftsreisende und Monteure, die eine komfortable
                            Unterkunft auf Zeit suchen.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-4 text-foreground/75 text-lg leading-relaxed max-w-2xl">
                            Genieße nach einem langen Arbeitstag eine ruhige
                            Atmosphäre, eine voll ausgestattete Küche und
                            ausreichend Platz zum Entspannen oder Arbeiten.
                        </p>
                    </Reveal>

                    <Reveal delay={0.2}>
                        <ul className="mt-8 flex flex-wrap gap-2.5">
                            {BUSINESS_ROLES.map((r) => (
                                <li
                                    key={r}
                                    data-testid="business-role-chip"
                                    className="rounded-full border border-primary/25 bg-background/60 px-4 py-2 text-sm text-foreground/80 backdrop-blur-sm"
                                >
                                    {r}
                                </li>
                            ))}
                        </ul>
                    </Reveal>

                    <Reveal delay={0.25}>
                        <div className="mt-8 grid sm:grid-cols-2 gap-4">
                            {HIGHLIGHTS.map((h) => (
                                <div
                                    key={h.title}
                                    className="flex gap-4 rounded-2xl bg-card border border-border/60 p-5"
                                >
                                    <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                        <h.icon size={18} strokeWidth={1.7} />
                                    </div>
                                    <div>
                                        <p className="font-medium text-foreground">
                                            {h.title}
                                        </p>
                                        <p className="mt-1 text-sm text-foreground/70 leading-relaxed">
                                            {h.text}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Reveal>

                    <Reveal delay={0.3}>
                        <div className="mt-8 flex flex-wrap items-center gap-4">
                            <a
                                href="#anfrage"
                                data-testid="business-inquiry-button"
                                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-base font-medium hover:bg-primary/90 transition-all duration-300 hover:-translate-y-0.5"
                            >
                                Als Firma / Berufsreisender anfragen
                            </a>
                            <span
                                data-testid="business-min-stay"
                                className="font-accent text-xs uppercase tracking-[0.25em] text-foreground/60"
                            >
                                Mindestaufenthalt · 3 Nächte
                            </span>
                        </div>
                    </Reveal>
                </div>

                <Reveal className="lg:col-span-5" delay={0.1}>
                    <div className="relative">
                        <div className="overflow-hidden rounded-3xl aspect-[4/5] shadow-[0_20px_60px_-24px_rgba(60,60,50,0.25)]">
                            <img
                                src={IMG}
                                alt="Arbeitsplatz mit Blick nach draußen im Emsglück"
                                loading="lazy"
                                className="h-full w-full object-cover transition-transform duration-[1400ms] hover:scale-105"
                            />
                        </div>
                        <div
                            aria-hidden
                            className="hidden md:block absolute -top-6 -right-6 w-28 h-28 rounded-full bg-primary/15 -z-10"
                        />
                        <div
                            aria-hidden
                            className="hidden md:block absolute -bottom-6 -left-6 w-40 h-40 rounded-3xl bg-secondary/60 -z-10"
                        />
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
